import { useEffect, useState } from "react";
import { Loader2, Scale } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  RBAC_SCOPE_CONSENT_VERSION,
  getApiErrorDetail,
  postRbacScopeAcceptanceConsent,
} from "@/lib/api";

const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";

const FLAG_PREFIX = "gsf:arbiter-acceptance:";

interface ArbiterCaseAcceptanceDialogProps {
  caseId: number;
  userId: string;
  // L'arbitre n'est invite a signer que s'il est rattache a CE
  // dossier avec le member_role=ARBITRE (lu depuis case_members
  // cote API). Un arbitre plateforme non constitue ne voit pas la
  // modale -- coherent avec le 3e verrou ACL OHADA T-03.
  isCaseArbiter: boolean;
  onAccepted?: () => void;
}

// Modale d'acceptation de mission arbitrale sur un dossier donne.
//
// Materialise le consentement RBAC_SCOPE_ACCEPTATION : type
// HIGH_STRENGTH cote core (TSA Zetes RFC 3161 + ancrage Merkle figeable
// dans la racine du dossier). Une fois pose, ce consentement scelle
// l'engagement arbitral pour la duree de la procedure -- preuve
// retracable 10 ans (duree legale arbitrage).
//
// Trois piliers signes (un checkbox chacun, tous obligatoires) :
// 1. Impartialite (pas de prejugement)
// 2. Independance (absence de conflit d'interet)
// 3. Secret du delibere (confidentialite)
//
// Flag localStorage par couple (userId, caseId) evite de re-prompter
// a chaque visite du dossier. Un changement de navigateur resoumet --
// le backend etant append-only sans contrainte d'unicite, c'est
// inoffensif (eventuellement audit double si l'arbitre re-confirme).
export function ArbiterCaseAcceptanceDialog({
  caseId,
  userId,
  isCaseArbiter,
  onAccepted,
}: ArbiterCaseAcceptanceDialogProps) {
  const [open, setOpen] = useState(false);
  const [impartial, setImpartial] = useState(false);
  const [independent, setIndependent] = useState(false);
  const [secret, setSecret] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isCaseArbiter) return;
    const flag = `${FLAG_PREFIX}${userId}:${caseId}`;
    if (!localStorage.getItem(flag)) {
      setOpen(true);
    }
  }, [caseId, userId, isCaseArbiter]);

  const allAccepted = impartial && independent && secret;

  const onConfirm = async () => {
    if (!allAccepted || submitting) return;
    setSubmitting(true);
    try {
      await postRbacScopeAcceptanceConsent(caseId);
      const flag = `${FLAG_PREFIX}${userId}:${caseId}`;
      localStorage.setItem(flag, new Date().toISOString());
      toast.success(
        "Acceptation de mission scellee. Materialisation TSA Zetes + Merkle effectuee.",
      );
      onAccepted?.();
      setOpen(false);
    } catch (err) {
      toast.error(
        getApiErrorDetail(err) ??
          "Echec de la materialisation. Reessayez.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-2xl p-0 gap-0 border-0"
        style={{
          background: MARINE_DEEP,
          border: `1px solid color-mix(in oklch, ${GOLD} 30%, transparent)`,
          color: "oklch(1 0 0)",
        }}
      >
        <DialogHeader className="px-8 pt-8 pb-3 space-y-3">
          <div
            className="w-12 h-12 flex items-center justify-center rounded-sm"
            style={{
              background: `color-mix(in oklch, ${GOLD} 18%, transparent)`,
              border: `1px solid color-mix(in oklch, ${GOLD} 50%, transparent)`,
            }}
          >
            <Scale size={20} style={{ color: GOLD }} />
          </div>
          <DialogTitle
            className="text-2xl text-left"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              letterSpacing: "0.01em",
            }}
          >
            Acceptation de la mission arbitrale
          </DialogTitle>
          <DialogDescription
            className="text-left text-sm"
            style={{ color: "oklch(0.85 0.01 243)", lineHeight: 1.55 }}
          >
            Vous etes constitue arbitre du dossier <strong>#{caseId}</strong>.
            Avant d'acceder aux fonctions de direction (validation
            d'etape, ancrage probatoire, signature de sentences), vous
            devez accepter formellement votre mission. Cette acceptation
            est materialisee par <strong>preuve forte</strong> (TSA
            qualifie eIDAS via Zetes + ancrage Merkle dans la racine du
            dossier) -- elle ne pourra etre revoquee qu'au titre des
            exceptions d'ordre public OHADA.
          </DialogDescription>
        </DialogHeader>

        <div
          className="px-8 py-4 text-sm space-y-4"
          style={{ color: "oklch(0.92 0.01 243)", lineHeight: 1.55 }}
        >
          <p>
            En cochant les trois engagements ci-dessous, vous declarez
            sur l'honneur :
          </p>

          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={impartial}
              onCheckedChange={(v) => setImpartial(v === true)}
              disabled={submitting}
              className="mt-0.5"
            />
            <span>
              <strong style={{ color: GOLD }}>Impartialite.</strong>{" "}
              Je n'ai pas de prejuge concernant le litige, ni de
              relation passee ou presente avec les parties susceptible
              d'affecter mon jugement. Je m'engage a revoyer cette
              declaration si une circonstance nouvelle l'exigeait.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={independent}
              onCheckedChange={(v) => setIndependent(v === true)}
              disabled={submitting}
              className="mt-0.5"
            />
            <span>
              <strong style={{ color: GOLD }}>Independance.</strong>{" "}
              Je n'ai aucun lien d'interet (financier, contractuel,
              familial) avec les parties, leurs conseils, ou tout
              tiers ayant un interet direct au litige. Je m'engage a
              divulguer immediatement tout conflit d'interet potentiel
              qui pourrait survenir au cours de la procedure.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={secret}
              onCheckedChange={(v) => setSecret(v === true)}
              disabled={submitting}
              className="mt-0.5"
            />
            <span>
              <strong style={{ color: GOLD }}>
                Secret du delibere.
              </strong>{" "}
              Je m'engage a ne reveler ni le contenu, ni le sens, ni les
              opinions exprimees au cours du delibere du tribunal
              arbitral. Cette obligation perdure apres la fin de la
              procedure et lie tous mes auxiliaires.
            </span>
          </label>

          <p
            className="text-xs pt-3 border-t"
            style={{
              color: "oklch(0.7 0.01 243)",
              borderColor: `color-mix(in oklch, ${GOLD} 12%, transparent)`,
            }}
          >
            Version du texte d'engagement :{" "}
            <code style={{ color: GOLD }}>
              {RBAC_SCOPE_CONSENT_VERSION}
            </code>
          </p>
        </div>

        <DialogFooter className="px-8 py-6">
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!allAccepted || submitting}
            style={{
              background: GOLD,
              color: MARINE_DEEP,
              opacity: !allAccepted || submitting ? 0.6 : 1,
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Materialisation TSA + Merkle...
              </>
            ) : (
              "Accepter la mission et signer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
